<template>
  <div class="collector-setup-container">
    <div class="collector-header">
      <h2>
        <i class="fas fa-qrcode"></i>
        QR Campaigns
      </h2>
      <div class="header-buttons">
        <button
          v-if="selectedCampaign"
          @click="refreshCampaignData"
          class="refresh-btn"
          title="Refresh campaign data"
        >
          <i class="fas fa-sync-alt"></i>
        </button>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <div class="collector-body">
      <!-- Campaign Selection and Delete Button -->
      <div class="section campaign-selector-row">
        <div class="select-group">
          <label for="campaignSelect">Select Campaign:</label>
          <div class="select-wrapper">
            <select
              id="campaignSelect"
              v-model="selectedCampaignId"
              @change="onCampaignChange"
            >
              <option :value="null">-- Select Campaign --</option>
              <option
                v-for="campaign in campaigns"
                :key="campaign.id"
                :value="campaign.id"
              >
                {{ campaign.title }} (Created:
                {{ formatDate(campaign.created_on) }})
              </option>
              <option value="NEW">+ New Campaign...</option>
            </select>
          </div>
        </div>
        <button
          v-if="selectedCampaign && !showCreateDialog"
          @click="confirmDelete"
          class="btn-danger btn-delete-inline"
        >
          <i class="fas fa-trash"></i> Delete Campaign
        </button>
      </div>

      <!-- Create Campaign Dialog -->
      <div v-if="showCreateDialog" class="create-dialog">
        <h3>Create New Campaign</h3>
        <p class="dialog-info">
          Enter a title for your campaign. The system will automatically append
          today's date and generate a unique QR code.
        </p>
        <div class="form-group">
          <label for="newTitle">Campaign Title:</label>
          <input
            id="newTitle"
            v-model="newCampaignTitle"
            type="text"
            placeholder="e.g., 218JohnBikes"
            maxlength="100"
            @keyup.enter="createCampaign"
            ref="titleInput"
          />
          <span v-if="createError" class="error-text">{{ createError }}</span>
        </div>
        <div class="form-group">
          <label for="newDescription">Description (optional):</label>
          <textarea
            id="newDescription"
            v-model="newCampaignDescription"
            placeholder="e.g., Please enter your bike information for building 218"
            maxlength="500"
            rows="3"
          ></textarea>
          <span class="helper-text"
            >This will be shown to users on the collection form</span
          >
        </div>
        <div class="form-group">
          <label for="newResponse1Label">Response Field Label:</label>
          <input
            id="newResponse1Label"
            v-model="newResponse1Label"
            type="text"
            placeholder="e.g., Tag Number, Serial Number, etc."
            maxlength="100"
          />
          <span class="helper-text"
            >Label for the first custom response field</span
          >
        </div>
        <div class="button-group">
          <button @click="createCampaign" class="btn-primary">Create</button>
          <button @click="cancelCreate" class="btn-secondary">Cancel</button>
        </div>
      </div>

      <!-- Campaign Details -->
      <div
        v-if="selectedCampaign && !showCreateDialog"
        class="campaign-details"
      >
        <!-- QR Code and Campaign Info Side by Side -->
        <div class="section qr-and-info-section">
          <div class="qr-and-info-row">
            <!-- QR Code Section -->
            <div class="qr-section">
              <h3>QR Code</h3>
              <div v-if="!qrDetailsCollapsed" class="qr-container">
                <div id="qrcode" ref="qrCodeContainer"></div>
              </div>
            </div>

            <!-- Campaign Info -->
            <div class="campaign-info">
              <h3 @click="toggleQrDetails" class="collapsible-header">
                Campaign Details
                <i
                  class="fas toggle-icon"
                  :class="
                    qrDetailsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'
                  "
                ></i>
              </h3>
              <div v-if="!qrDetailsCollapsed">
                <div class="info-row">
                  <span class="label">Campaign Title:</span>
                  <span
                    v-if="!editingField || editingField !== 'title'"
                    class="value editable"
                    @dblclick="startEditing('title')"
                    :title="'Double-click to edit'"
                  >
                    {{ selectedCampaign.title }}
                  </span>
                  <input
                    v-else
                    v-model="editValue"
                    @blur="saveEdit('title')"
                    @keyup.enter="saveEdit('title')"
                    @keyup.esc="cancelEdit"
                    ref="editInput"
                    class="edit-input"
                    maxlength="100"
                  />
                  <span class="label" style="margin-left: 20px"
                    >Response field label:</span
                  >
                  <span
                    v-if="!editingField || editingField !== 'response_1_label'"
                    class="value editable"
                    @dblclick="startEditing('response_1_label')"
                    :title="'Double-click to edit'"
                  >
                    {{ selectedCampaign.response_1_label || 'Tag Number' }}
                  </span>
                  <input
                    v-else
                    v-model="editValue"
                    @blur="saveEdit('response_1_label')"
                    @keyup.enter="saveEdit('response_1_label')"
                    @keyup.esc="cancelEdit"
                    ref="editResponse1Label"
                    class="edit-input"
                    maxlength="100"
                  />
                </div>
                <div class="info-row description-row">
                  <span class="label">Description:</span>
                  <span
                    v-if="!editingField || editingField !== 'description'"
                    class="value editable"
                    @dblclick="startEditing('description')"
                    :title="'Double-click to edit'"
                  >
                    {{
                      selectedCampaign.description ||
                      '(No description - click to add)'
                    }}
                  </span>
                  <input
                    v-else
                    v-model="editValue"
                    @blur="saveEdit('description')"
                    @keyup.enter="saveEdit('description')"
                    @keyup.esc="cancelEdit"
                    ref="editTextarea"
                    class="edit-input"
                    maxlength="500"
                  />
                </div>
                <div class="info-row">
                  <span class="label">Created By:</span>
                  <span class="value">{{ selectedCampaign.created_by }}</span>
                  <span class="label" style="margin-left: 20px"
                    >Created On:</span
                  >
                  <span class="value">{{
                    formatDate(selectedCampaign.created_on)
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Responses:</span>
                  <span class="value">{{ responses.length }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Download Button and URL -->
          <div v-if="!qrDetailsCollapsed" class="qr-url-section">
            <button @click="downloadQR" class="btn-secondary btn-download">
              <i class="fas fa-download"></i> Download QR Code
            </button>
            <div class="qr-url">
              <strong>URL:&nbsp;</strong>
              <a :href="collectorUrl" target="_blank">{{ collectorUrl }}</a>
            </div>
          </div>
        </div>

        <!-- Responses Table -->
        <div class="section responses-section">
          <h3>Responses ({{ responses.length }})</h3>
          <div v-if="responses.length === 0" class="no-responses">
            No responses yet.
          </div>
          <div v-else class="responses-table-wrapper">
            <table class="responses-table">
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Apartment</th>
                  <th
                    class="response-col"
                    :title="selectedCampaign.response_1_label || 'Tag Number'"
                  >
                    {{ selectedCampaign.response_1_label || 'Tag Number' }}
                  </th>
                  <th class="comment-col">Comment</th>
                  <th>IP Address</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="response in responses" :key="response.id">
                  <td>{{ formatDateTime(response.submitted_on) }}</td>
                  <td>{{ response.first_name }} {{ response.last_name }}</td>
                  <td>{{ formatPhone(response.phone) }}</td>
                  <td class="apartment-col">
                    <span
                      v-if="
                        !editingResponseId ||
                        editingResponseId !== response.id ||
                        editingResponseField !== 'apartment'
                      "
                      class="editable-cell"
                      @dblclick="
                        startEditingResponseField(response, 'apartment')
                      "
                      :title="response.apartment"
                    >
                      {{ response.apartment }}
                    </span>
                    <input
                      v-else
                      v-model="editResponseValue"
                      @blur="saveResponseFieldEdit(response, 'apartment')"
                      @keyup.enter="
                        saveResponseFieldEdit(response, 'apartment')
                      "
                      @keyup.esc="cancelResponseEdit"
                      ref="editApartmentInput"
                      class="response-edit-input"
                      maxlength="50"
                    />
                  </td>
                  <td class="response-col">
                    <span
                      v-if="
                        !editingResponseId ||
                        editingResponseId !== response.id ||
                        editingResponseField !== 'response_1'
                      "
                      class="editable-cell"
                      @dblclick="
                        startEditingResponseField(response, 'response_1')
                      "
                      :title="response.response_1"
                    >
                      {{ response.response_1 }}
                    </span>
                    <input
                      v-else
                      v-model="editResponseValue"
                      @blur="saveResponseFieldEdit(response, 'response_1')"
                      @keyup.enter="
                        saveResponseFieldEdit(response, 'response_1')
                      "
                      @keyup.esc="cancelResponseEdit"
                      ref="editResponseInput"
                      class="response-edit-input"
                      maxlength="100"
                    />
                  </td>
                  <td class="comment-col" :title="response.comment || '-'">
                    {{ response.comment || '-' }}
                  </td>
                  <td class="ip-cell">{{ response.ip_address }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Dialog -->
      <div v-if="showDeleteConfirm" class="delete-confirm">
        <h3>Confirm Delete</h3>
        <p>
          Are you sure you want to delete the campaign
          <strong>{{ selectedCampaign.title }}</strong
          >?
        </p>
        <p class="warning-text">
          <i class="fas fa-exclamation-triangle"></i>
          This will permanently delete all {{ responses.length }} responses.
        </p>
        <div class="button-group">
          <button @click="deleteCampaign" class="btn-danger">
            Yes, Delete
          </button>
          <button @click="showDeleteConfirm = false" class="btn-secondary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosConfig from '@/axiosConfig';
import QRCode from 'qrcode';
import eventBus from './eventBus';

export default {
  name: 'CollectorSetup',

  data() {
    return {
      campaigns: [],
      selectedCampaignId: null,
      selectedCampaign: null,
      responses: [],
      showCreateDialog: false,
      newCampaignTitle: '',
      newCampaignDescription: '',
      createError: null,
      showDeleteConfirm: false,
      collectorUrl: '',
      editingField: null,
      editValue: '',
      editingResponseId: null,
      editingResponseField: null,
      editResponseValue: '',
      qrDetailsCollapsed: false,
    };
  },

  async mounted() {
    await this.loadCampaigns();

    // Load last selected campaign from localStorage
    const lastSelectedId = localStorage.getItem('collectorLastCampaignId');
    if (lastSelectedId && this.campaigns.length > 0) {
      const campaign = this.campaigns.find(
        (c) => c.id === parseInt(lastSelectedId),
      );
      if (campaign) {
        this.selectedCampaignId = campaign.id;
        await this.onCampaignChange();
      }
    }

    // Register EventBus listeners for real-time updates
    eventBus.on('collectorCampaignCreated', this.onCampaignCreated);
    eventBus.on('collectorCampaignUpdated', this.onCampaignUpdated);
    eventBus.on('collectorCampaignDeleted', this.onCampaignDeleted);
    eventBus.on('collectorResponseCreated', this.onResponseCreated);
  },

  beforeUnmount() {
    // Clean up EventBus listeners
    eventBus.off('collectorCampaignCreated', this.onCampaignCreated);
    eventBus.off('collectorCampaignUpdated', this.onCampaignUpdated);
    eventBus.off('collectorCampaignDeleted', this.onCampaignDeleted);
    eventBus.off('collectorResponseCreated', this.onResponseCreated);
  },

  methods: {
    toggleQrDetails() {
      this.qrDetailsCollapsed = !this.qrDetailsCollapsed;

      // Regenerate QR code when expanding
      if (!this.qrDetailsCollapsed && this.selectedCampaign) {
        this.$nextTick(() => {
          this.generateQRCode();
        });
      }
    },

    async loadCampaigns() {
      try {
        const response = await axiosConfig.get('/api/collector/campaigns');
        this.campaigns = response.data;
      } catch (error) {
        console.error('Error loading campaigns:', error);
        alert('Failed to load campaigns. Please try again.');
      }
    },

    async refreshCampaignData() {
      if (!this.selectedCampaignId) return;

      try {
        // Reload the selected campaign data
        await this.onCampaignChange();
      } catch (error) {
        console.error('Error refreshing campaign data:', error);
        alert('Failed to refresh campaign data. Please try again.');
      }
    },

    async onCampaignChange() {
      if (this.selectedCampaignId === 'NEW') {
        this.showCreateDialog = true;
        this.newCampaignTitle = '';
        this.newCampaignDescription = '';
        this.createError = null;
        this.selectedCampaignId = null;
        // Focus the input after Vue updates DOM
        this.$nextTick(() => {
          if (this.$refs.titleInput) {
            this.$refs.titleInput.focus();
          }
        });
        return;
      }

      if (this.selectedCampaignId) {
        // Save selected campaign to localStorage
        localStorage.setItem(
          'collectorLastCampaignId',
          this.selectedCampaignId.toString(),
        );

        this.selectedCampaign = this.campaigns.find(
          (c) => c.id === this.selectedCampaignId,
        );
        await this.loadResponses();
        this.generateQRCode();
      } else {
        this.selectedCampaign = null;
        this.responses = [];
      }
    },

    async loadResponses() {
      if (!this.selectedCampaignId) return;

      try {
        const response = await axiosConfig.get(
          `/api/collector/responses/${this.selectedCampaignId}`,
        );
        this.responses = response.data;
      } catch (error) {
        console.error('Error loading responses:', error);
        alert('Failed to load responses. Please try again.');
      }
    },

    async createCampaign() {
      if (!this.newCampaignTitle.trim()) {
        this.createError = 'Campaign title is required';
        return;
      }

      try {
        const response = await axiosConfig.post('/api/collector/campaigns', {
          title: this.newCampaignTitle.trim(),
          description: this.newCampaignDescription.trim() || null,
        });

        // Reload campaigns and select the new one
        await this.loadCampaigns();
        this.selectedCampaignId = response.data.id;
        this.showCreateDialog = false;
        await this.onCampaignChange();
      } catch (error) {
        console.error('Error creating campaign:', error);
        this.createError =
          error.response?.data?.error ||
          'Failed to create campaign. Please try again.';
      }
    },

    cancelCreate() {
      this.showCreateDialog = false;
      this.newCampaignTitle = '';
      this.newCampaignDescription = '';
      this.createError = null;
    },

    confirmDelete() {
      this.showDeleteConfirm = true;
    },

    async deleteCampaign() {
      if (!this.selectedCampaignId) return;

      try {
        await axiosConfig.delete(
          `/api/collector/campaigns/${this.selectedCampaignId}`,
        );

        // Clear selection and reload campaigns
        this.selectedCampaignId = null;
        this.selectedCampaign = null;
        this.responses = [];
        this.showDeleteConfirm = false;
        await this.loadCampaigns();

        alert('Campaign deleted successfully.');
      } catch (error) {
        console.error('Error deleting campaign:', error);
        alert('Failed to delete campaign. Please try again.');
      }
    },

    generateQRCode() {
      if (!this.selectedCampaign) return;

      // Build the production URL (always production for both QR code and display)
      const productionUrl = `https://pixie.bigtapapp.com/collector?id=${this.selectedCampaign.hash_id}`;
      this.collectorUrl = productionUrl;

      // Clear previous QR code
      const container = this.$refs.qrCodeContainer;
      if (container) {
        container.innerHTML = '';

        // Generate new QR code using production URL
        QRCode.toCanvas(
          productionUrl,
          {
            width: 150,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF',
            },
          },
          (error, canvas) => {
            if (error) {
              console.error('QR Code generation error:', error);
              return;
            }
            container.appendChild(canvas);
          },
        );
      }
    },

    downloadQR() {
      const canvas = this.$refs.qrCodeContainer.querySelector('canvas');
      if (!canvas) return;

      // Create download link
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `qr-${this.selectedCampaign.campaign_string}.png`;
      link.href = url;
      link.click();
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatPhone(phone) {
      if (!phone || phone.length !== 10) return phone;
      return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
    },

    // In-place editing methods
    startEditing(field) {
      this.editingField = field;
      this.editValue = this.selectedCampaign[field] || '';
      this.$nextTick(() => {
        let inputRef;
        if (field === 'title') {
          inputRef = this.$refs.editInput;
        } else if (field === 'response_1_label') {
          inputRef = this.$refs.editResponse1Label;
        } else {
          inputRef = this.$refs.editTextarea;
        }
        if (inputRef) {
          inputRef.focus();
          inputRef.select();
        }
      });
    },

    cancelEdit() {
      this.editingField = null;
      this.editValue = '';
    },

    async saveEdit(field) {
      // Don't save if value hasn't changed
      if (this.editValue === (this.selectedCampaign[field] || '')) {
        this.cancelEdit();
        return;
      }

      try {
        const updateData = {};
        updateData[field] = this.editValue.trim() || null;

        await axiosConfig.patch(
          `/api/collector/campaigns/${this.selectedCampaignId}`,
          updateData,
        );

        // Update local data
        this.selectedCampaign[field] = this.editValue.trim() || null;

        // Update in campaigns list
        const campaignIndex = this.campaigns.findIndex(
          (c) => c.id === this.selectedCampaignId,
        );
        if (campaignIndex !== -1) {
          this.campaigns[campaignIndex][field] = this.editValue.trim() || null;
        }

        this.cancelEdit();
      } catch (error) {
        console.error('Error updating campaign:', error);
        alert('Failed to update campaign. Please try again.');
        this.cancelEdit();
      }
    },

    // Response editing methods
    startEditingResponseField(response, field) {
      this.editingResponseId = response.id;
      this.editingResponseField = field;
      this.editResponseValue = response[field] || '';
      this.$nextTick(() => {
        const inputRef =
          field === 'apartment'
            ? this.$refs.editApartmentInput
            : this.$refs.editResponseInput;
        // Refs in v-for are arrays, so we need to access the first element
        const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },

    cancelResponseEdit() {
      this.editingResponseId = null;
      this.editingResponseField = null;
      this.editResponseValue = '';
    },

    async saveResponseFieldEdit(response, field) {
      // Don't save if value hasn't changed
      if (this.editResponseValue === (response[field] || '')) {
        this.cancelResponseEdit();
        return;
      }

      try {
        const updateData = {};
        updateData[field] = this.editResponseValue.trim() || null;

        await axiosConfig.patch(
          `/api/collector/responses/${response.id}`,
          updateData,
        );

        // Update local data
        const responseIndex = this.responses.findIndex(
          (r) => r.id === response.id,
        );
        if (responseIndex !== -1) {
          this.responses[responseIndex][field] =
            this.editResponseValue.trim() || null;
        }

        this.cancelResponseEdit();
      } catch (error) {
        console.error('Error updating response:', error);
        alert('Failed to update response. Please try again.');
        this.cancelResponseEdit();
      }
    },

    // Real-time event handlers
    async onCampaignCreated(data) {
      console.log('📢 CollectorSetup: Campaign created event:', data);
      // Reload campaigns list
      await this.loadCampaigns();
      // Auto-select the newly created campaign
      if (data.id) {
        this.selectedCampaignId = data.id;
        await this.onCampaignChange();
      }
    },

    onCampaignUpdated(data) {
      console.log('📢 CollectorSetup: Campaign updated event:', data);
      // If the updated campaign is currently selected, update it
      if (data.id === this.selectedCampaignId && this.selectedCampaign) {
        if (data.title !== undefined) {
          this.selectedCampaign.title = data.title;
        }
        if (data.description !== undefined) {
          this.selectedCampaign.description = data.description;
        }
        if (data.response_1_label !== undefined) {
          this.selectedCampaign.response_1_label = data.response_1_label;
        }
      }
      // Update in campaigns list
      const campaignIndex = this.campaigns.findIndex((c) => c.id === data.id);
      if (campaignIndex !== -1) {
        if (data.title !== undefined) {
          this.campaigns[campaignIndex].title = data.title;
        }
        if (data.description !== undefined) {
          this.campaigns[campaignIndex].description = data.description;
        }
        if (data.response_1_label !== undefined) {
          this.campaigns[campaignIndex].response_1_label =
            data.response_1_label;
        }
      }
    },

    async onCampaignDeleted(data) {
      console.log('📢 CollectorSetup: Campaign deleted event:', data);
      // If the deleted campaign is currently selected, clear selection
      if (data.id === this.selectedCampaignId) {
        this.selectedCampaignId = null;
        this.selectedCampaign = null;
        this.responses = [];
      }
      // Remove from campaigns list
      this.campaigns = this.campaigns.filter((c) => c.id !== data.id);
    },

    async onResponseCreated(data) {
      console.log('📢 CollectorSetup: Response created event:', data);
      // If the response is for the currently selected campaign, reload responses
      if (data.campaignId === this.selectedCampaignId) {
        await this.loadResponses();
      }
    },
  },
};
</script>

<style scoped>
.collector-setup-container {
  background: white;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.collector-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #6e5875;
  color: white;
  flex-shrink: 0;
  position: relative;
}

.collector-header h2 {
  margin: 0;
  font-size: 1.2em;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-buttons {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 8px;
  align-items: center;
}

.refresh-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.1em;
  cursor: pointer;
  padding: 5px 8px;
  border-radius: 4px;
  transition: all 0.3s;
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: rotate(180deg);
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.2em;
  cursor: pointer;
  padding: 5px 8px;
  border-radius: 4px;
  transition: background 0.3s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.collector-body {
  padding: 15px;
  overflow-y: auto;
  flex: 1;
  /* Invisible scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.collector-body::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.section {
  margin-bottom: 0px;
}

.section h3 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1em;
  border-bottom: 2px solid #a080a8;
  padding-bottom: 5px;
}

label {
  display: block;
  margin-bottom: 6px;
  color: #333;
  font-weight: 600;
  font-size: 0.9em;
}

.campaign-selector-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.select-group {
  flex: 1;
}

.btn-delete-inline {
  flex-shrink: 0;
  white-space: nowrap;
  margin-bottom: 0;
}

.select-wrapper {
  position: relative;
}

select {
  width: 100%;
  padding: 8px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9em;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s;
}

select:focus {
  outline: none;
  border-color: #a080a8;
}

.create-dialog {
  background: #f9f9f9;
  padding: 15px;
  border-radius: 6px;
  border: 2px solid #a080a8;
  margin-bottom: 15px;
}

.create-dialog h3 {
  margin: 0 0 8px 0;
  color: #a080a8;
  font-size: 1em;
}

.dialog-info {
  color: #666;
  font-size: 0.85em;
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 15px;
}

input,
textarea {
  width: 100%;
  padding: 8px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9em;
  transition: border-color 0.3s;
  box-sizing: border-box;
  font-family: inherit;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: #a080a8;
}

textarea {
  resize: vertical;
  min-height: 60px;
}

.helper-text {
  color: #888;
  font-size: 0.8em;
  margin-top: 4px;
  display: block;
}

.error-text {
  color: #f44336;
  font-size: 0.85em;
  margin-top: 4px;
  display: block;
}

.button-group {
  display: flex;
  gap: 8px;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: #a080a8;
  color: white;
  flex: 1;
}

.btn-primary:hover {
  background: #8a6a8f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(160, 128, 168, 0.4);
}

.btn-secondary {
  background: #f0f0f0;
  color: #666;
  flex: 1;
}

.btn-secondary:hover {
  background: #e0e0e0;
}

.btn-danger {
  background: #a080a8;
  color: white;
}

.btn-danger:hover {
  background: #8a6a8f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(160, 128, 168, 0.4);
}

.campaign-details {
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.qr-and-info-section {
  background: #f9f9f9;
  padding: 12px;
  border-radius: 6px;
  padding-bottom: 0px;
}

.qr-and-info-row {
  display: flex;
  gap: 15px;
  align-items: flex-start;
}

.collapsible-header {
  cursor: pointer;
  user-select: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.collapsible-header:hover {
  opacity: 0.8;
}

.toggle-icon {
  color: #a080a8;
  font-size: 0.8em;
  margin-left: 8px;
  transition: transform 0.3s;
}

.qr-section {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
}

.qr-section h3 {
  margin: 0 0 10px 0;
  font-size: 0.95em;
  color: #a080a8;
}

.qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

#qrcode {
  display: flex;
  justify-content: center;
  align-items: center;
}

#qrcode canvas {
  max-width: 150px;
  height: auto;
}

.campaign-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0px;
}

.campaign-info h3 {
  margin: 0 0 10px 0;
  font-size: 0.95em;
  color: #a080a8;
}

.qr-url-section {
  background: #f9f9f9;
  padding: 10px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%;
}

.btn-download {
  flex-shrink: 0;
  white-space: nowrap;
}

.qr-url {
  flex: 1;
  text-align: left;
  font-size: 0.85em;
  color: #666;
  display: flex;
  align-items: baseline;
}

.qr-url strong {
  flex-shrink: 0;
}

.qr-url a {
  color: #a080a8;
  text-decoration: none;
  word-break: break-all;
  padding-left: 0;
}

.qr-url a:hover {
  text-decoration: underline;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid #e0e0e0;
  font-size: 0.9em;
}

.info-row.description-row {
  flex-direction: column;
  gap: 4px;
}

.info-row.description-row .value {
  font-style: italic;
  color: #666;
}

.info-row.description-row .value.editable:empty::before {
  content: '(No description - click to add)';
  color: #999;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  font-weight: 600;
  color: #666;
  flex-shrink: 0;
}

.info-row .value {
  color: #333;
  flex: 1;
  min-height: 1.3em;
  padding: 2px 12px;
  margin: -2px -4px;
  border-radius: 3px;
}

.info-row .value.editable {
  cursor: pointer;
  transition: background 0.2s;
}

.info-row .value.editable:hover {
  background: #f0f0f0;
}

.edit-input,
.edit-textarea {
  flex: 1;
  padding: 2px 4px;
  margin: -2px -4px;
  border: 2px solid #a080a8;
  border-radius: 3px;
  font-size: 0.9em;
  font-family: inherit;
  color: #333;
  background: white;
  min-height: 1.3em;
  line-height: 1.3;
}

.edit-input:focus,
.edit-textarea:focus {
  outline: none;
  border-color: #8a6a8f;
}

.edit-textarea {
  resize: vertical;
  min-height: 60px;
  font-style: normal;
}

.responses-section {
  margin-top: 15px;
}

.no-responses {
  text-align: center;
  color: #999;
  padding: 20px;
  font-style: italic;
  font-size: 0.9em;
}

.responses-table-wrapper {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  max-height: 300px;
  overflow-y: auto;
  /* Invisible scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.responses-table-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.responses-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8em;
}

.responses-table thead {
  background: #a080a8;
  color: white;
  position: sticky;
  top: 0;
  /* z-index: 10; */
}

.responses-table th {
  padding: 8px 6px;
  text-align: left;
  font-weight: 600;
  white-space: nowrap;
  font-size: 0.85em;
}

.responses-table td {
  padding: 6px;
  border-bottom: 1px solid #e0e0e0;
}

.responses-table tbody tr:hover {
  background: #f5f5f5;
}

.ip-cell {
  font-family: monospace;
  font-size: 0.8em;
  color: #666;
}

.comment-col {
  max-width: 170px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.response-col {
  max-width: 50px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.apartment-col {
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.editable-cell {
  display: block;
  cursor: pointer;
  padding: 2px 4px;
  margin: -2px -4px;
  border-radius: 3px;
  transition: background 0.2s;
  min-height: 1em;
}

.editable-cell:hover {
  background: rgba(160, 128, 168, 0.1);
}

.response-edit-input {
  width: 100%;
  padding: 2px 4px;
  margin: -2px -4px;
  border: 2px solid #a080a8;
  border-radius: 3px;
  font-size: inherit;
  font-family: inherit;
  color: inherit;
  background: white;
  box-sizing: border-box;
  outline: none;
}

.response-edit-input:focus {
  border-color: #8a6a8f;
}

.delete-confirm {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  z-index: 10001;
  max-width: 320px;
  width: 90%;
}

.delete-confirm h3 {
  margin: 0 0 12px 0;
  color: #f44336;
  font-size: 1em;
}

.delete-confirm p {
  margin: 8px 0;
  color: #333;
  font-size: 0.9em;
}

.warning-text {
  color: #f44336;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
}
</style>
